import { DecimalPipe, NgClass } from '@angular/common'
import { HttpErrorResponse } from '@angular/common/http'
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { ProgressCircleComponent } from '@app/v2/shared/components/progress-circle/progress-circle.component'
import { TruncationTooltipComponent } from '@app/v2/shared/components/truncation-tooltip/truncation-tooltip.component'
import { Alert } from '@app/v2/shared/enums/alert.enum'
import { SafeDomPipe } from '@app/v2/shared/pipes/safe-dom.pipe'
import { AlertService } from '@app/v2/shared/services/alert.service'
import { AppService } from '@app/v2/shared/services/app.service'
import { QueuedJobsService } from '@app/v2/shared/services/queued-jobs.service'
import { ErrorResponse } from '@app/v2/shared/types/generic-response.type'
import { QueuedJobModel } from '@app/v2/shared/types/queued-job.model'
import { QUEUED_JOB_CHANNEL_ICONS, QUEUED_JOB_ICONS, QueuedJob } from '@app/v2/shared/types/queued-job.type'
import { NgxTippyModule } from 'ngx-tippy-wrapper'
import { finalize } from 'rxjs'

@Component({
  selector: 'app-queued-job-card',
  standalone: true,
  imports: [NgClass, DecimalPipe, SafeDomPipe, NgxTippyModule, ProgressCircleComponent, TruncationTooltipComponent],
  templateUrl: './queued-job-card.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'tw-block' }
})
export class QueuedJobCardComponent {
  public readonly job = input.required<QueuedJob>()

  public readonly icons = QUEUED_JOB_ICONS
  public readonly skippedLabel = QueuedJobModel.SKIPPED_LABEL
  public readonly skippedDetailsLabel = QueuedJobModel.SKIPPED_DETAILS_LABEL
  public readonly downloadReportLabel = QueuedJobModel.DOWNLOAD_REPORT_LABEL
  public readonly title = computed(() => QueuedJobModel.TITLE_BY_TYPE[this.job().type])
  public readonly resultLabel = computed(() => QueuedJobModel.RESULT_LABEL_BY_TYPE[this.job().type])
  public readonly channelIcon = computed(() => {
    const channel = this.job().channel
    return channel ? QUEUED_JOB_CHANNEL_ICONS[channel] : null
  })
  public readonly chips = computed(() => [this.job().event_name, this.job().persona_name])
  public readonly skippedReasons = computed(() => QueuedJobModel.skippedReasons(this.job()))
  public readonly processedCount = computed(() => QueuedJobModel.processedCount(this.job()))
  public readonly progressPercent = computed(() => QueuedJobModel.progressPercent(this.job()))
  public readonly failedMessage = computed(() => this.job().message || QueuedJobModel.FAILED_FALLBACK_MESSAGE)
  public readonly canDownloadReport = computed(() => QueuedJobModel.canDownloadReport(this.job()))
  public readonly isDownloading = signal<boolean>(false)

  private readonly _queuedJobsService = inject(QueuedJobsService)
  private readonly _alertService = inject(AlertService)
  private readonly _appService = inject(AppService)
  private readonly _destroyRef = inject(DestroyRef)

  /**
   * Downloads the job's report file; extra clicks are ignored while a download is in progress.
   */
  public downloadReport(): void {
    const fileUrl = this.job().file_url
    if (!fileUrl || this.isDownloading()) return

    const filename = QueuedJobModel.reportFilename(fileUrl)
    this.isDownloading.set(true)
    this._queuedJobsService
      .downloadReport(fileUrl)
      .pipe(
        finalize(() => this.isDownloading.set(false)),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: (blob) => this._appService.downloadBlob(blob, filename),
        error: (error: HttpErrorResponse) => this._alertDownloadError(error)
      })
  }

  /**
   * Shows the API error message in an alert. Blob requests return their error body as a Blob, so it is read as JSON first.
   * @param error {HttpErrorResponse} - Failed download response.
   */
  private async _alertDownloadError(error: HttpErrorResponse): Promise<void> {
    let message: string | undefined
    try {
      const body = error.error instanceof Blob ? JSON.parse(await error.error.text()) : error.error
      message = (body as ErrorResponse | null)?.message
    } catch {
      message = undefined
    }
    this._alertService.alert(Alert.ERROR, message || QueuedJobModel.DOWNLOAD_REPORT_ERROR_FALLBACK_MESSAGE)
  }
}
